import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  fetchDashboardSummary,
  fetchSimulator,
  updateSlider,
  runSensitivity,
} from './bie.apis';

import {
  IBieDashboardPayload,
  IBieScenarioOption,
  IBieSimulatorPayload,
} from './bie.models';
import DashboardCards from './DashboardCards';
import BreakdownPanel from './BreakdownPanel';
import MarginBridge from './MarginBridge';
import DriverMixer from './DriverMixer';

import BieWorkbenchFilterBar from './BieWorkbenchFilterBar';
import BieExecutiveDecisionWorkbenchPanel from './BieExecutiveDecisionWorkbenchPanel';
import SensitivityTable from './SensitivityTable';
import ImportWizard from './ImportWizard';
import BieSimulatorAssumptionsPanel from './BieSimulatorAssumptionsPanel';
import BieDashboardCardsPanel from './BieDashboardCardsPanel';
import { ComboRow } from '../core/coremodels';
import { fetchBieScenarioCombo } from './bieApiModern';

type State = {
  scenario: string;
  scenarios: ComboRow[];
  scenarioOptions: IBieScenarioOption[];
  loading: boolean;
  error?: string;
  dashboard?: IBieDashboardPayload;
  simulator?: IBieSimulatorPayload;
  sensitivity?: any[];
  success?: string;
};

class BieWorkbench extends React.Component<any, State> {
  state: State = {
    scenario: 'DET2027_BASE',
    scenarios: [],
    loading: false,
    scenarioOptions:[],
    success: '',
  };

  onChange = (name: any) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e?.target?.value;
    console.log('event', e);
    const value = name === 'scenario' ? raw?.toUpperCase() : raw;
    this.setState({ [name]: value } as Pick<State, any>);
  };

  async componentDidMount() {
    await this.loadCombos();
    await this.load();
    //await this.init();
  }
  
  loadCombos = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [scenarios] = await Promise.all([fetchBieScenarioCombo()]);
      this.setState({ scenarios });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  filters = () => {
    const {scenario} = this.state;

    return {
      scc: scenario || null,
    };
  };

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const { scc: scenario } = this.filters();
      if (!scenario) return;
      const [dashboards, simulators] = await Promise.all([
        fetchDashboardSummary(scenario),
        fetchSimulator(scenario).catch(() => []),
      ]);

      this.setState({
        dashboard: dashboards as IBieDashboardPayload,
        simulator: simulators as IBieSimulatorPayload,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSliderCommit = async (drivercode: string, value: number) => {
    try {
      this.setState({ loading: true, error: undefined });
      await updateSlider(this.state.scenario, drivercode, value);
      await this.load();
    } catch (e: any) {
      this.setState({ error: e.message || String(e), loading: false });
    }
  };

  handleSensitivity = async () => {
    try {
      this.setState({ loading: true, error: undefined });
      const res: any = await runSensitivity(
        this.state.scenario,
        'RESPARTVAL',
        10,
      );
      const payload = res.sd ? res : res.sd || res;
      this.setState({ sensitivity: payload.sd || [] });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  applyScenario = async () => {
    this.setState({ success: 'Scenario application workflow is not yet wired.' });
  };

  render() {
    const { loading, scenario, scenarios, error, dashboard, simulator, sensitivity } = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant='h4' fontWeight={800}>
              BIE Simulation Workbench
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Budget Impact Engine · Scenario analytics · Driver mixer
            </Typography>
          </Box>

          <BieWorkbenchFilterBar
            scenario={scenario}
            scenarios = {scenarios}
            loading = {loading}
            onScenarioChange={(v: string) => this.setState({ scenario: v })}
            onRefresh = {this.load}
            onRunSensitivity={this.handleSensitivity}
            //onOpenImport = {}
          />
        </Stack>

        <BieSimulatorAssumptionsPanel
          scenarioCode={this.state.scenario || 'DET2027_BASE'}
        />

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Paper
            sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <CircularProgress size={20} />
            <Typography variant='body2'>Computing scenario...</Typography>
          </Paper>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <DashboardCards cards={dashboard?.cards || simulator?.kpi} />
            <BieDashboardCardsPanel dashboard={dashboard} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <BreakdownPanel
              title='Revenue Breakdown'
              rows={dashboard?.revenue || []}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <BreakdownPanel
              title='Cost Breakdown'
              rows={dashboard?.costs || []}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            <MarginBridge rows={dashboard?.bridge || []} />
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <BieExecutiveDecisionWorkbenchPanel
              scenarioCode={this.state.scenario}
              // summary={this.state.summary || this.state.cards || {}}
              // objectiveGaps={this.state.objectiveGapRows || this.state.objectiveGaps || []}
              // constraints={this.state.constraints || this.state.alerts || []}
              loading={this.state.loading}
              onApplyScenario={this.applyScenario}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            <DriverMixer
              drivers={simulator?.drivers || []}
              disabled={loading}
              onChangeCommitted={this.handleSliderCommit}
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <SensitivityTable
              rows={sensitivity || dashboard?.sensitivity || []}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ImportWizard
              scenario={this.state.scenario}
              onImported={this.load}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default BieWorkbench;
